import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  approveAgentPlan,
  cancelAgentPlan,
  cleanupWithAgent,
  describeWithAgent,
  organizeWithAgent,
  sendAgentMessage,
  smartFindWithAgent,
  summarizeFolderWithAgent,
  type AgentQuickPayload,
} from "@/api/agent";
import type { AgentChatResponse } from "@/api/types";
import { useAgentSSE } from "@/hooks/useAgentSSE";
import { useAgentStore } from "@/store/agentStore";

type QuickAction = "organize" | "summarize" | "find" | "cleanup" | "describe";

function transcriptId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getAssistantMessage(response: AgentChatResponse) {
  if (response.assistant_message?.trim()) {
    return response.assistant_message.trim();
  }

  switch (response.status) {
    case "awaiting_approval":
      return "I prepared a plan for you. Review it below and approve anything that changes files.";
    case "executing":
      return "I started executing the approved plan.";
    case "completed":
      return "The AI task completed successfully.";
    case "cancelled":
      return "The AI task was cancelled.";
    case "failed":
      return "The AI task could not be completed.";
    default:
      return "I updated the session.";
  }
}

export function useAgent() {
  const panelOpen = useAgentStore((state) => state.panelOpen);
  const activeSessionId = useAgentStore((state) => state.activeSessionId);
  const transcript = useAgentStore((state) => state.transcript);
  const lastResponse = useAgentStore((state) => state.lastResponse);
  const openPanel = useAgentStore((state) => state.openPanel);
  const closePanel = useAgentStore((state) => state.closePanel);
  const togglePanel = useAgentStore((state) => state.togglePanel);
  const setActiveSessionId = useAgentStore((state) => state.setActiveSessionId);
  const addTranscriptEntry = useAgentStore((state) => state.addTranscriptEntry);
  const setLastResponse = useAgentStore((state) => state.setLastResponse);
  const clearConversation = useAgentStore((state) => state.clearConversation);

  const statusQuery = useAgentSSE(activeSessionId);

  const syncResponse = (response: AgentChatResponse) => {
    setActiveSessionId(response.session_id);
    setLastResponse(response);
  };

  const chatMutation = useMutation({
    mutationFn: (message: string) =>
      sendAgentMessage({
        session_id: activeSessionId ?? undefined,
        message,
      }),
    onMutate: (message) => {
      openPanel();
      addTranscriptEntry({
        id: transcriptId("user"),
        role: "user",
        content: message,
        createdAt: new Date().toISOString(),
        sessionId: activeSessionId ?? undefined,
      });
    },
    onSuccess: (response) => {
      syncResponse(response);
      addTranscriptEntry({
        id: transcriptId("assistant"),
        role: "assistant",
        content: getAssistantMessage(response),
        createdAt: new Date().toISOString(),
        sessionId: response.session_id,
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "AI assistant request failed.");
    },
  });

  const approveMutation = useMutation({
    mutationFn: (sessionId: string) => approveAgentPlan(sessionId),
    onSuccess: (response) => {
      syncResponse(response);
      addTranscriptEntry({
        id: transcriptId("assistant"),
        role: "assistant",
        content: getAssistantMessage(response),
        createdAt: new Date().toISOString(),
        sessionId: response.session_id,
      });
      toast.success("Plan approved.");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not approve the plan.");
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (sessionId: string) => cancelAgentPlan(sessionId),
    onSuccess: (response) => {
      syncResponse(response);
      addTranscriptEntry({
        id: transcriptId("assistant"),
        role: "assistant",
        content: getAssistantMessage(response),
        createdAt: new Date().toISOString(),
        sessionId: response.session_id,
      });
      toast.success("Session cancelled.");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not cancel the session.");
    },
  });

  const quickMutation = useMutation({
    mutationFn: async ({ action, payload }: { action: QuickAction; payload: AgentQuickPayload }) => {
      switch (action) {
        case "organize":
          return organizeWithAgent(payload);
        case "summarize":
          return summarizeFolderWithAgent(payload);
        case "find":
          return smartFindWithAgent(payload);
        case "cleanup":
          return cleanupWithAgent(payload);
        case "describe":
          return describeWithAgent(payload);
        default:
          throw new Error("Unsupported AI action");
      }
    },
    onSuccess: (response) => {
      syncResponse(response);
      addTranscriptEntry({
        id: transcriptId("assistant"),
        role: "assistant",
        content: getAssistantMessage(response),
        createdAt: new Date().toISOString(),
        sessionId: response.session_id,
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not run the AI action.");
    },
  });

  return {
    panelOpen,
    transcript,
    activeSessionId,
    lastResponse: statusQuery.data ?? lastResponse,
    isBusy:
      chatMutation.isPending ||
      approveMutation.isPending ||
      cancelMutation.isPending ||
      quickMutation.isPending,
    openPanel,
    closePanel,
    togglePanel,
    clearConversation,
    sendMessage: (message: string) => chatMutation.mutate(message),
    approvePlan: () => {
      if (!activeSessionId) {
        return;
      }
      approveMutation.mutate(activeSessionId);
    },
    cancelSession: () => {
      if (!activeSessionId) {
        return;
      }
      cancelMutation.mutate(activeSessionId);
    },
    runQuickAction: (action: QuickAction, payload: AgentQuickPayload = {}) => {
      openPanel();
      quickMutation.mutate({ action, payload });
    },
  };
}
